import React, { useState, useEffect } from 'react';
import {
  Modal,
  Form,
  Input,
  Select,
  TimePicker,
  Row,
  Col,
  Button,
  Typography,
  Spin,
  message,
  Card,
  Descriptions,
  Tag,
  Space,
  Grid,
  Divider
} from 'antd';
import { createBranch, updateBranch, fetchBranchById, fetchUsers } from '../api';
import { BranchFormFields } from '../components';
import BranchUsers from '../components/BranchUsers';
import { usePermissions } from '../../../hooks/usePermissions';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;
const { useBreakpoint } = Grid;

const BranchCreateAndViewDetails = ({ 
  visible, 
  mode, 
  branchData, 
  onClose, 
  onSuccess,
  onModeChange // Add this prop to handle mode changes
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [branchDetails, setBranchDetails] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);

  const screens = useBreakpoint();
  const { canEditModule, canViewCreateModule, can } = usePermissions();

  const isViewMode = mode === 'view';
  const isEditMode = mode === 'edit';
  const isCreateMode = mode === 'create';

  const statusOptions = [
    { label: 'Active', value: 'active' },
    { label: 'Inactive', value: 'inactive' },
    { label: 'Under Maintenance', value: 'under_maintenance' },
    { label: 'Closed', value: 'closed' },
  ];

  const workingDaysOptions = [
    'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'
  ];

  useEffect(() => {
    if (visible) {
      loadUsers();
      if (isViewMode || isEditMode) {
        loadBranchDetails();
      } else if (isCreateMode) {
        form.resetFields();
        setBranchDetails(null);
      }
    }
  }, [visible, mode, branchData]);

  const loadUsers = async () => {
    setUsersLoading(true);
    try {
      const response = await fetchUsers({ limit: 100 });
      setUsers(response.data || []);
    } catch (error) {
      console.error('Error loading users:', error);
      message.error('Failed to load users for manager selection');
    } finally {
      setUsersLoading(false);
    }
  };

  const loadBranchDetails = async () => {
    if (!branchData?._id) {
      setBranchDetails(branchData);
      if (isEditMode) {
        populateForm(branchData);
      }
      return;
    }

    setDetailsLoading(true);
    try {
      const response = await fetchBranchById(branchData._id);
      const details = response.data || response;
      setBranchDetails(details);
      
      if (isEditMode) {
        populateForm(details);
      }
    } catch (error) {
      console.error('Error loading branch details:', error);
      message.error('Failed to load branch details');
      setBranchDetails(branchData);
      if (isEditMode) {
        populateForm(branchData);
      }
    } finally {
      setDetailsLoading(false);
    }
  };

  const populateForm = (data) => {
    const formData = {
      name: data.name,
      code: data.code,
      'address.street': data.address?.street,
      'address.city': data.address?.city,
      'address.state': data.address?.state,
      'address.country': data.address?.country || 'India',
      'address.postalCode': data.address?.postalCode,
      'address.landmark': data.address?.landmark,
      'contactDetails.phone': data.contactDetails?.phone,
      'contactDetails.email': data.contactDetails?.email,
      'contactDetails.fax': data.contactDetails?.fax,
      manager: data.manager?._id || data.manager,
      'operationalHours.openTime': data.operationalHours?.openTime ? dayjs(data.operationalHours.openTime, 'HH:mm') : null,
      'operationalHours.closeTime': data.operationalHours?.closeTime ? dayjs(data.operationalHours.closeTime, 'HH:mm') : null,
      'operationalHours.workingDays': data.operationalHours?.workingDays || [],
      status: data.status || 'active',
      branchType: data.branchType || 'BRANCH',
    };

    form.setFieldsValue(formData);
  };

  const handleSubmit = async (values) => {
    setLoading(true);
    try {
      const formattedData = {
        name: values.name,
        code: values.code?.toUpperCase(),
        address: {
          street: values['address.street'],
          city: values['address.city'],
          state: values['address.state'],
          country: values['address.country'] || 'India',
          postalCode: values['address.postalCode'],
          landmark: values['address.landmark'],
        },
        contactDetails: {
          phone: values['contactDetails.phone'],
          email: values['contactDetails.email'],
          fax: values['contactDetails.fax'],
        },
        manager: values.manager,
        operationalHours: {
          openTime: values['operationalHours.openTime']?.format('HH:mm'),
          closeTime: values['operationalHours.closeTime']?.format('HH:mm'),
          workingDays: values['operationalHours.workingDays'],
        },
        status: values.status,
        branchType: values.branchType,
      };

      if (isCreateMode) {
        await createBranch(formattedData);
        message.success('Branch created successfully');
      } else if (isEditMode) {
        await updateBranch(branchData._id, formattedData);
        message.success('Branch updated successfully');
      }

      onSuccess();
    } catch (error) {
      console.error('Error saving branch:', error);
      const errorMessage = error.response?.data?.message || error.message || 'An error occurred';
      message.error(`Failed to ${isCreateMode ? 'create' : 'update'} branch: ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'green';
      case 'inactive': return 'orange';
      case 'under_maintenance': return 'blue';
      case 'closed': return 'red';
      default: return 'default';
    }
  };

  const renderViewContent = () => {
    if (detailsLoading) {
      return (
        <div style={{ textAlign: 'center', padding: '50px 0' }}>
          <Spin size="large" />
          <div style={{ marginTop: 16 }}>Loading branch details...</div>
        </div>
      );
    }

    if (!branchDetails) {
      return (
        <div style={{ textAlign: 'center', padding: '50px 0' }}>
          <Text>No branch details available</Text>
        </div>
      );
    }

    return (
      <div>
        <Descriptions 
          bordered 
          column={screens.xs ? 1 : 2}
          size={screens.xs ? 'small' : 'default'}
        >
          <Descriptions.Item label="Branch Name" span={screens.xs ? 1 : 2}>
            <Space>
              <Text strong style={{ fontSize: '16px' }}>{branchDetails.name}</Text>
              <Tag color={getStatusColor(branchDetails.status)} style={{ textTransform: 'capitalize' }}>
                {branchDetails.status}
              </Tag>
            </Space>
          </Descriptions.Item>
          
          <Descriptions.Item label="Branch Code">
            <Text code>{branchDetails.code}</Text>
          </Descriptions.Item>

          <Descriptions.Item label="Branch Type">
            {branchDetails.branchType === 'HO' ? 'Head Office (HO)' : 'Branch'}
          </Descriptions.Item>
          
          <Descriptions.Item label="Manager">
            {branchDetails.manager?.name || branchDetails.manager?.email || '-'}
          </Descriptions.Item>

          {branchDetails.address && (
            <>
              <Descriptions.Item label="Street Address" span={screens.xs ? 1 : 2}>
                {branchDetails.address.street || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="City">
                {branchDetails.address.city || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="State">
                {branchDetails.address.state || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="Country">
                {branchDetails.address.country || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="Postal Code">
                {branchDetails.address.postalCode || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="Region">
                {branchDetails.address.region || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="District">
                {branchDetails.address.district || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="Division">
                {branchDetails.address.division || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="Circle">
                {branchDetails.address.circle || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="Landmark" span={screens.xs ? 1 : 2}>
                {branchDetails.address.landmark || '-'}
              </Descriptions.Item>
            </>
          )}

          {branchDetails.contactDetails && (
            <>
              <Descriptions.Item label="Phone">
                {branchDetails.contactDetails.phone || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="Email">
                {branchDetails.contactDetails.email || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="Fax" span={screens.xs ? 1 : 2}>
                {branchDetails.contactDetails.fax || '-'}
              </Descriptions.Item>
            </>
          )}

          {branchDetails.operationalHours && (
            <>
              <Descriptions.Item label="Opening Time">
                {branchDetails.operationalHours.openTime || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="Closing Time">
                {branchDetails.operationalHours.closeTime || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="Working Days" span={screens.xs ? 1 : 2}>
                {branchDetails.operationalHours.workingDays?.length ? 
                  branchDetails.operationalHours.workingDays.join(', ') : '-'}
              </Descriptions.Item>
            </>
          )}

          <Descriptions.Item label="Created">
            {branchDetails.createdAt ? new Date(branchDetails.createdAt).toLocaleDateString() : '-'}
          </Descriptions.Item>
          <Descriptions.Item label="Last Updated">
            {branchDetails.updatedAt ? new Date(branchDetails.updatedAt).toLocaleDateString() : '-'}
          </Descriptions.Item>
        </Descriptions>

        <Divider />
        
        <BranchUsers 
          branchId={branchDetails._id} 
          branchName={branchDetails.name} 
        />
      </div>
    );
  };

  const renderFormContent = () => (
    <Form
      form={form}
      layout="vertical"
      onFinish={handleSubmit}
      requiredMark={false}
    >
      <BranchFormFields 
        form={form} 
        users={users} 
        usersLoading={usersLoading} 
      />
    </Form>
  );

  const getModalTitle = () => {
    switch (mode) {
      case 'create': return 'Create New Branch';
      case 'edit': return 'Edit Branch';
      case 'view': return 'Branch Details';
      default: return 'Branch';
    }
  };

  const getModalFooter = () => {
    if (isViewMode) {
      return [
        <Button key="close" onClick={onClose}>
          Close
        </Button>,
        ...(canEditModule('branches') ? [
          <Button 
            key="edit" 
            type="primary" 
            onClick={() => {
              // Switch to edit mode by calling the parent's mode change handler
              if (onModeChange) {
                onModeChange('edit', branchDetails);
              }
            }}
          >
            Edit Branch
          </Button>
        ] : [])
      ];
    }

    return [
      <Button key="cancel" onClick={onClose}>
        Cancel
      </Button>,
      <Button
        key="submit"
        type="primary"
        loading={loading}
        onClick={() => form.submit()}
      >
        {isCreateMode ? 'Create Branch' : 'Update Branch'}
      </Button>,
    ];
  };

  const canShowModal = () => {
    if (isCreateMode) return canViewCreateModule('branches');
    if (isEditMode) return canEditModule('branches');
    return true; // View mode is generally allowed
  };

  if (!canShowModal()) {
    return null;
  }

  return (
    <Modal
      title={getModalTitle()}
      open={visible}
      onCancel={onClose}
      footer={getModalFooter()}
      width={screens.xs ? '95%' : screens.sm ? '80%' : 900}
      centered
      destroyOnClose
      maskClosable={false}
    >
      <Spin spinning={loading}>
        {isViewMode ? renderViewContent() : renderFormContent()}
      </Spin>
    </Modal>
  );
};

export default BranchCreateAndViewDetails;
